import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** T278 row as GET /v1/audit/logs/:logId returns it, inside a { success, data } envelope. */
interface AuditDetail {
  readonly logId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly actorIp: string | null;
  readonly beforeState: Record<string, unknown> | null;
  readonly afterState: Record<string, unknown> | null;
  readonly eventTimestamp: string;
}

export function AuditDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const [data, setData] = useState<AuditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data?: AuditDetail } | AuditDetail>(`/v1/audit/logs/${id}`, { token })
      .then((r) => {
        if (!alive) return;
        const row = (r as { data?: AuditDetail }).data ?? (r as AuditDetail);
        setData(row);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('audit.detail.loading')}</p>;
  return (
    <main aria-label="audit-detail">
      <h1>{t('audit.detail.title')}</h1>
      <dl>
        <dt>{t('audit.detail.event')}</dt><dd aria-label="event-type">{data.eventType}</dd>
        <dt>{t('audit.detail.action')}</dt><dd aria-label="action">{data.action}</dd>
        <dt>{t('audit.detail.actor')}</dt><dd aria-label="actor">{personLabel(data.actorName, data.actorCode) ?? '—'}</dd>
        <dt>{t('audit.detail.ip')}</dt><dd aria-label="actor-ip">{data.actorIp ?? '-'}</dd>
        <dt>{t('audit.detail.entity')}</dt><dd aria-label="entity">{data.entityType}</dd>
        <dt>{t('audit.detail.when')}</dt><dd aria-label="when">{at(data.eventTimestamp)}</dd>
      </dl>
      <h2>{t('audit.detail.before')}</h2>
      <pre aria-label="before">{JSON.stringify(data.beforeState ?? {}, null, 2)}</pre>
      <h2>{t('audit.detail.after')}</h2>
      <pre aria-label="after">{JSON.stringify(data.afterState ?? {}, null, 2)}</pre>
    </main>
  );
}
